import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { usePopupStore } from '@/stores/popup'
import type {
  TProjectBackupRecord,
  TProjectHistoryEntry,
  TStoredProjectBackupRecord,
  TVersionHistoryEntry,
} from '@/types/projects'

const BACKUPS_DB_NAME = 'DesignrrProjectBackups'
const BACKUPS_DB_VERSION = 2
const OPEN_TIMEOUT_MS = 5000

export const useProjectBackupStore = defineStore('project-backups', () => {
  const popup = usePopupStore()
  const localItems = ref<TProjectBackupRecord[]>([])
  const remoteItems = ref<TProjectHistoryEntry[]>([])

  let openingDb: Promise<IDBDatabase | null> | null = null
  let closeOtherTabsAsked = false

  const entries = computed<TVersionHistoryEntry[]>(() => {
    const local = localItems.value.map((entry) => ({
      local: true as const,
      time: new Date(entry.timestamp).getTime() || 0,
      entry,
    }))
    const remote = remoteItems.value.map((entry) => ({
      local: false as const,
      time: new Date(entry.created_at).getTime() || 0,
      entry,
    }))

    return [...local, ...remote].sort((a, b) => b.time - a.time)
  })

  /**
   * Open the backups database once and resolve with it, or with null when IndexedDB is unavailable, fails to open,
   * or is held at an older version by another tab.
   */
  function openIndexedDb(): Promise<IDBDatabase | null> {
    if (!window.indexedDB) {
      return Promise.resolve(null)
    }

    openingDb ??= new Promise((resolve) => {
      const request = indexedDB.open(BACKUPS_DB_NAME, BACKUPS_DB_VERSION)
      const giveUp = () => {
        askToCloseOtherTabs()
        resolve(null)
      }
      const unanswered = setTimeout(giveUp, OPEN_TIMEOUT_MS)

      request.onerror = (event: Event) => {
        clearTimeout(unanswered)
        console.log('IndexedDB error')
        console.log(event)
        openingDb = null
        resolve(null)
      }

      request.onblocked = () => {
        clearTimeout(unanswered)
        giveUp()
      }

      request.onsuccess = () => {
        clearTimeout(unanswered)
        const db = request.result
        db.onversionchange = () => {
          db.close()
          openingDb = null
        }

        openingDb = Promise.resolve(db)
        resolve(db)
      }

      request.onupgradeneeded = (event: { oldVersion: number }) => {
        if (event.oldVersion < 1) {
          const objectStore = request.result.createObjectStore('projects', { autoIncrement: true })
          objectStore.createIndex('timestamp', 'timestamp', { unique: true })
          objectStore.createIndex('projectId', 'projectId', { unique: false })

          return
        }

        const objectStore = request.transaction!.objectStore('projects')
        for (const indexName of ['name', 'html', 'css']) {
          if (objectStore.indexNames.contains(indexName)) {
            objectStore.deleteIndex(indexName)
          }
        }
      }
    })

    return openingDb
  }

  function askToCloseOtherTabs() {
    if (closeOtherTabsAsked) {
      return
    }

    closeOtherTabsAsked = true
    popup.alert('Designrr has been updated', {
      content:
        'Please close all other Designrr tabs and reload this page, so the update can finish and your browser backups keep working.',
    })
  }

  async function decodeRecord(record: TStoredProjectBackupRecord): Promise<TProjectBackupRecord> {
    if (!('content' in record)) {
      return record
    }

    const { content, ...rest } = record
    const stream = new Response(content).body!.pipeThrough(new DecompressionStream('gzip'))
    const decoded: { html: string; css: string } = JSON.parse(await new Response(stream).text())

    return { ...rest, ...decoded }
  }

  async function getLocalEntries(projectId: number): Promise<void> {
    localItems.value = []

    const db = await openIndexedDb()
    if (!db) {
      return
    }

    const records = await new Promise<TStoredProjectBackupRecord[]>((resolve, reject) => {
      const request = db
        .transaction(['projects'], 'readonly')
        .objectStore('projects')
        .index('projectId')
        .getAll(IDBKeyRange.only(projectId))
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error)
    })
    localItems.value = await Promise.all(records.map(decodeRecord))
  }

  async function getRemoteEntries(projectId: number): Promise<void> {
    remoteItems.value = []

    const res = await fetch(`/projects/${projectId}/get-project-history-entries`, {
      method: 'POST',
    })
    if (!res.ok) {
      throw new Error(`Failed to fetch history entries`)
    }
    remoteItems.value = await res.json()
  }

  async function getEntryContent(entryId: number): Promise<{ html: string; css: string }> {
    const res = await fetch(`/projects/${entryId}/get-history-entry-content`, {
      method: 'POST',
    })
    if (!res.ok) {
      throw new Error(`Failed to fetch history entry content`)
    }
    return await res.json()
  }

  return { openIndexedDb, getLocalEntries, getRemoteEntries, getEntryContent, entries }
})
